import { useEffect, useRef, useState } from 'react';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, Typography } from '@mui/material';
import indigencyTemplate from '../resources/certificates/NEW-BARANGAY-INDIGENCY.docx1.pdf';
import residencyTemplate from '../resources/certificates/NEW-CERTIFICATION-OF-RESIDENCY.docx1.pdf';
import clearanceTemplate from '../resources/certificates/NEW-BARANGAY-CLEARANCE-1.docx1.pdf';
import './CertificatePreviewDialog.css';

const CERTIFICATE_TEMPLATES = {
  indigency: {
    title: 'Certificate of Indigency',
    templateFile: indigencyTemplate,
    fields: {
      name: { x: 270, y: 512, size: 12, maxWidth: 260 }
    },
  },
  residency: {
    title: 'Certificate of Residency',
    templateFile: residencyTemplate,
    fields: {
      name: { x: 300, y: 490, size: 12, maxWidth: 270 },
      address: { x: 240, y: 470, size: 20, maxWidth: 270 },
    },
  },
  clearance: {
    title: 'Barangay Clearance',
    templateFile: clearanceTemplate,
    fields: {
      name: { x: 300, y: 518, size: 12, maxWidth: 270 }
    },
  },
};

const formatIssuedDate = () => new Date().toLocaleDateString('en-PH', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
});

const fitTextSize = (font, text, maxWidth, preferredSize) => {
  let size = preferredSize;

  while (size > 7 && font.widthOfTextAtSize(text, size) > maxWidth) {
    size -= 0.5;
  }

  return size;
};

const drawField = (page, font, text, field) => {
  if (!field || !text) {
    return;
  }

  const size = fitTextSize(font, text, field.maxWidth, field.size);

  page.drawText(text, {
    x: field.x,
    y: field.y,
    size,
    font,
    color: rgb(0, 0, 0),
  });
};

async function createFilledCertificatePdf(template, resident) {
  const response = await fetch(template.templateFile);

  if (!response.ok) {
    throw new Error('Could not load the certificate PDF template.');
  }

  const templateBytes = await response.arrayBuffer();
  const pdfDoc = await PDFDocument.load(templateBytes);
  const font = await pdfDoc.embedFont(StandardFonts.TimesRomanBold);
  const [page] = pdfDoc.getPages();
  const residentName = resident.name || 'Resident Name';
  const residentAddress = resident.address || 'Resident Address';

  drawField(page, font, residentName, template.fields.name);
  drawField(page, font, residentAddress, template.fields.address);
  drawField(page, font, formatIssuedDate(), template.fields.issuedDate);

  return pdfDoc.save();
}

function CertificatePreviewDialog({ open, onClose, resident, type = 'indigency' }) {
  const iframeRef = useRef(null);
  const [pdfUrl, setPdfUrl] = useState('');
  const [status, setStatus] = useState(null);
  const template = CERTIFICATE_TEMPLATES[type] || CERTIFICATE_TEMPLATES.indigency;

  useEffect(() => {
    if (!open || !resident) {
      setPdfUrl('');
      setStatus(null);
      return undefined;
    }

    let cancelled = false;
    let nextUrl = '';

    async function generatePdf() {
      setStatus({ severity: 'info', message: 'Preparing certificate PDF...' });

      try {
        const pdfBytes = await createFilledCertificatePdf(template, resident);
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        nextUrl = URL.createObjectURL(blob);

        if (!cancelled) {
          setPdfUrl(nextUrl);
          setStatus(null);
        }
      } catch (error) {
        if (!cancelled) {
          setStatus({ severity: 'error', message: error.message });
        }
      }
    }

    generatePdf();

    return () => {
      cancelled = true;
      if (nextUrl) {
        URL.revokeObjectURL(nextUrl);
      }
    };
  }, [open, resident, template]);

  if (!resident) {
    return null;
  }

  const handlePrint = () => {
    iframeRef.current?.contentWindow?.focus();
    iframeRef.current?.contentWindow?.print();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      className="certificate-print-dialog"
    >
      <DialogContent className="certificate-dialog-content">
        <Box className="certificate-preview-header">
          <Typography variant="h6" className="certificate-preview-title">
            {template.title}
          </Typography>
          <Typography className="certificate-preview-copy">
            Generated for {resident.name || 'resident'} using the uploaded PDF template.
          </Typography>
        </Box>

        {status && (
          <Alert severity={status.severity} className="certificate-alert">
            {status.message}
          </Alert>
        )}

        {pdfUrl && (
          <iframe
            ref={iframeRef}
            className="certificate-pdf-frame"
            src={pdfUrl}
            title={`${template.title} preview`}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={handlePrint} disabled={!pdfUrl}>
          Print PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CertificatePreviewDialog;
